import React, { useEffect, useState, useCallback } from "react";
import Swal from "sweetalert2";
import axiosWithAuth from "../../../../utils/axiosWithAuth";
import "./RoomTypeManager.scss";

const RoomTypeManager = () => {
  const [roomTypeAdmin, setRoomTypeAdmin] = useState([]);
  const [roomType, setRoomType] = useState({
    name: { az: "", en: "", ru: "" },
    space: "",
    iconType: "",
    status: true,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [space, setSpace] = useState([]);
  const [loading, setLoading] = useState(false);
  const authToken = localStorage.getItem("authToken");

  const fetchRoomTypes = useCallback(() => {
    if (!authToken) return;
    
    setLoading(true);
    const api = axiosWithAuth();
    api.get("/room-types/admin")
      .then((response) => {
        setRoomTypeAdmin(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
        Swal.fire({
          title: "Error",
          text: "Failed to fetch room types. Please try again.",
          icon: "error",
          timer: 1500,
        });
      });
  }, [authToken]);

  const fetchSpace = useCallback(() => {
    if (!authToken) return;
    
    setLoading(true);
    const api = axiosWithAuth();
    api.get("/spaces/admin")
      .then((response) => {
        setSpace(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Failed to fetch spaces:", error);
        setLoading(false);
        Swal.fire({
          title: "Error",
          text: "Failed to fetch spaces. Please try again.",
          icon: "error",
          timer: 1500,
        });
      });
  }, [authToken]);

  useEffect(() => {
    if (!authToken) {
      Swal.fire({
        title: "Error",
        text: "You need to login first",
        icon: "error",
        timer: 1500,
      });
      return;
    }
    
    // Kullanıcı durumunu kontrol et
    const api = axiosWithAuth();
    api.get("/users/profile")
      .then((response) => {
        if (response.data.status !== 'active') {
          Swal.fire({
            title: "Error",
            text: "Your account is inactive. Please contact support.",
            icon: "error",
            timer: 3000,
          });
          return;
        }
        fetchRoomTypes();
        fetchSpace();
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          title: "Error",
          text: "Failed to verify user status. Please try again.",
          icon: "error",
          timer: 1500,
        });
      });
  }, [authToken, fetchRoomTypes, fetchSpace]);

  const handleSpaceChange = (event) => {
    setRoomType((prevRoomType) => ({
      ...prevRoomType,
      space: event.target.value,
    }));
  };

  const handleInput = (event) => {
    const { name, value, dataset } = event.target;
    const field = dataset.field;

    if (["az", "en", "ru"].includes(name)) {
      if (field === "name") {
        setRoomType((prevType) => ({
          ...prevType,
          name: { ...prevType.name, [name]: value },
        }));
      } else if (field === "spaceName") {
        setRoomType((prevType) => ({
          ...prevType,
          space: {
            ...prevType.space,
            name: { ...prevType.space.name, [name]: value },
          },
        }));
      }
    } else {
      setRoomType((prevType) => ({ ...prevType, [name]: value }));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!authToken) return;

    setLoading(true);
    const api = axiosWithAuth();
    const method = isEditing ? 'put' : 'post';
    const url = isEditing
      ? `/room-types/${editId}`
      : "/room-types";

    api[method](url, roomType)
      .then(() => {
        setIsEditing(false);
        setEditId(null);
        setRoomType({
          name: { az: "", en: "", ru: "" },
          space: "",
          iconType: "",
          status: true,
        });
        fetchRoomTypes();
        setLoading(false);
        Swal.fire({
          title: "Success",
          text: isEditing ? "Room type has been updated" : "Room type has been added",
          icon: "success",
          timer: 1500,
        });
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
        Swal.fire({
          title: "Error",
          text: isEditing ? "Failed to update room type" : "Failed to add room type",
          icon: "error",
          timer: 1500,
        });
      });
  };

  const handleEdit = (type) => {
    setRoomType(type);
    setEditId(type.id);
    setIsEditing(true);
  };

  const handleDelete = (id) => {
    if (!authToken) return;

    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#cda742",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel"
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        const api = axiosWithAuth();
        api.delete(`/room-types/${id}`)
          .then(() => {
            // Silinen kaydı listeden kaldır
            setRoomTypeAdmin(prevTypes => prevTypes.filter(type => type.id !== id));
            setLoading(false);
            Swal.fire({
              title: "Deleted!",
              text: "Room type has been deleted.",
              icon: "success",
              timer: 1500,
            });
          })
          .catch((error) => {
            console.log(error);
            setLoading(false);
            Swal.fire({
              title: "Error",
              text: "Failed to delete room type",
              icon: "error",
              timer: 1500,
            });
          });
      }
    });
  };

  return (
    <div className="room-types-manager">
      <div className="container">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Room Types Manager</h3>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit} className="d-flex flex-column gap-3">
                  <div className="room-types-name">
                    <h5 className="section-title">Room Types Name</h5>
                    <div className="row g-3">
                      <div className="col-md-4">
                        <div className="form-group">
                          <label className="form-label" htmlFor="az">
                            Azerbaijani
                          </label>
                          <input
                            className="form-control"
                            type="text"
                            value={roomType.name.az}
                            onChange={handleInput}
                            name="az"
                            data-field="name"
                            required
                            placeholder="Enter Azerbaijani name"
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label className="form-label" htmlFor="en">
                            English
                          </label>
                          <input
                            className="form-control"
                            type="text"
                            value={roomType.name.en}
                            onChange={handleInput}
                            name="en"
                            data-field="name"
                            required
                            placeholder="Enter English name"
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label className="form-label" htmlFor="ru">
                            Russian
                          </label>
                          <input
                            className="form-control"
                            type="text"
                            value={roomType.name.ru}
                            onChange={handleInput}
                            name="ru"
                            data-field="name"
                            required
                            placeholder="Enter Russian name"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="room-types-space-name">
                    <h5 className="section-title">Space Select</h5>
                    <div className="row">
                      <div className="col-12">
                        <div className="form-group">
                          <select
                            className="form-select"
                            value={roomType.space}
                            onChange={handleSpaceChange}
                            required
                          >
                            <option value="" disabled>
                              Select Space
                            </option>
                            {space.map((space) => (
                              <option key={space.id} value={space.id}>
                                {space.name.az}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="room-types-icon-type">
                    <h5 className="section-title">Icon Type</h5>
                    <div className="row">
                      <div className="col-12">
                        <div className="form-group">
                          <input
                            className="form-control"
                            type="text"
                            value={roomType.iconType}
                            onChange={handleInput}
                            name="iconType"
                            required
                            placeholder="Enter icon type"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary submit-btn">
                      {isEditing ? "Update Room Type" : "Add Room Type"}
                    </button>
                    {isEditing && (
                      <button
                        type="button"
                        className="btn btn-secondary cancel-btn"
                        onClick={() => {
                          setIsEditing(false);
                          setEditId(null);
                          setRoomType({
                            name: { az: "", en: "", ru: "" },
                            space: "",
                            iconType: "",
                            status: true,
                          });
                        }}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div className="row mt-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Room Types List</h3>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Name (AZ)</th>
                        <th>Name (EN)</th>
                        <th>Name (RU)</th>
                        <th>Space</th>
                        <th>Icon Type</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roomTypeAdmin.map((type) => (
                        <tr key={type.id}>
                          <td>{type.name.az}</td>
                          <td>{type.name.en}</td>
                          <td>{type.name.ru}</td>
                          <td>{type.space?.name?.az || "N/A"}</td>
                          <td>{type.iconType}</td>
                          <td>
                            <span className={`status-badge ${type.status ? "active" : "inactive"}`}>
                              {type.status ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="btn btn-sm btn-primary edit-btn"
                                onClick={() => handleEdit(type)}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-sm btn-danger delete-btn"
                                onClick={() => handleDelete(type.id)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomTypeManager;
